import React from 'react';
import { Cart } from '../utils/cart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';

interface CartSummaryProps {
  cart: Cart;
  showCheckoutButton?: boolean;
  isLoading?: boolean;
  onCheckout?: () => void;
}

const CartSummary: React.FC<CartSummaryProps> = ({
  cart,
  showCheckoutButton = false,
  isLoading = false,
  onCheckout
}) => {
  // Calculate shipping cost (example logic)
  const shippingCost = cart.totalPrice > 100 ? 0 : 10;
  
  // Calculate tax (example @ 8%)
  const taxRate = 0.08;
  const taxAmount = cart.totalPrice * taxRate;
  
  // Calculate order total
  const orderTotal = cart.totalPrice + shippingCost + taxAmount;

  return (
    <div className="bg-background-light rounded-lg shadow-md p-6">
      <h2 className="text-lg font-bold mb-4">Order Summary</h2>
      
      {/* Items summary */}
      <div className="space-y-3 mb-4">
        {cart.items.map((item) => (
          <div key={item.id} className="flex justify-between text-sm">
            <div className="flex items-start">
              <span className="font-medium">{item.quantity} ×</span>
              <span className="ml-2 line-clamp-1">{item.name}</span>
            </div>
            <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
          </div>
        ))}
      </div>
      
      {/* Divider */}
      <div className="border-t border-background-dark/10 my-4"></div>
      
      {/* Price calculations */}
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span>Subtotal</span>
          <span className="font-medium">${cart.totalPrice.toFixed(2)}</span>
        </div>
        
        <div className="flex justify-between">
          <span>Shipping</span>
          <span className="font-medium">
            {shippingCost === 0 ? 'Free' : `$${shippingCost.toFixed(2)}`}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span>Tax (est.)</span>
          <span className="font-medium">${taxAmount.toFixed(2)}</span>
        </div>
      </div>
      
      {/* Divider */}
      <div className="border-t border-background-dark/10 my-4"></div>
      
      {/* Total */}
      <div className="flex justify-between mb-6">
        <span className="font-bold">Total</span>
        <span className="font-bold text-lg text-accent">${orderTotal.toFixed(2)}</span>
      </div>
      
      {/* Checkout button */}
      {showCheckoutButton && onCheckout && (
        <button
          onClick={onCheckout}
          disabled={isLoading || cart.items.length === 0}
          className="w-full py-3 bg-purple-600 text-white rounded font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center justify-center"
        >
          {isLoading ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : (
            <span className="flex items-center">
              <FontAwesomeIcon icon={faLock} className="mr-2 h-4 w-4" />
              Secure Checkout
            </span>
          )}
        </button>
      )}
      
      {/* Security message */}
      <div className="text-xs text-center text-secondary mt-4 flex items-center justify-center">
        <FontAwesomeIcon icon={faLock} className="mr-1 h-3 w-3" />
        <span>All transactions are secure and encrypted</span>
      </div>
    </div>
  );
};

export default CartSummary; 